"use client";

// ============================================================
// CLUBHUB – Mein Bereich: Beitraege, Rechnungen, Lastschriftmandat
// ============================================================
// Was offen ist und wann es abgebucht wird, steht oben — mit dem
// Konto, von dem es abgeht. Darunter die Belege aller Personen des
// Zugangs (wer zahlt, sieht alles, was er zahlt), je Beleg ein Blatt
// mit Positionen und dem Weg zum Bezahlen.
//
// Bankverbindung aendern heisst: NEUES Mandat (neue Referenz),
// erteilt online mit vollem Mandatstext. Das alte gilt bis dahin
// und endet mit dem neuen.
//
// BACKEND: Rechnungen aus Invoice (+ InvoiceItem, Payment); Mandate
// aus SepaMandate; neues Mandat → SepaMandate (signatureMethod
// ONLINE, Nachweis, amendedFrom = altes Mandat), altes → REVOKED.
// ============================================================
import { useState } from "react";
import { ArrowRight, CircleCheck, Copy, Download, FileText, Landmark, ShieldCheck } from "lucide-react";
import type { InvoiceStatus } from "@/generated/prisma/enums";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitleRow } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogCancel, DialogHeader, DialogSubmit, DialogTitle, DialogNotice } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { EmptyState } from "@/components/ui/empty-state";
import { Seitenblatt } from "@/components/ui/sheet";
import { confirmDialog } from "@/components/ui/confirm";
import { toast } from "@/components/ui/toast";
import { Schiene, Zaehlknopf } from "@/components/liste";
import { TonBadge } from "@/components/mitglieder/status-badge";
import { SepaEingabe, SEPA_LEER } from "@/components/formulare/sepa-eingabe";
import { FEE_INTERVAL, INVOICE_KIND, INVOICE_STATUS, MANDATE_STATUS, MANDATE_TYPE, PAYMENT_METHOD } from "@/lib/labels";
import { cn, formatEuro, formatDate } from "@/lib/utils";
import { feldFehler, ibanLesbar, ibanVerdeckt } from "@/lib/formulare/pruefen";
import { erstattungWochen, mandatsText, vorabTage } from "@/lib/formulare/einwilligungen";
import { tagKurz } from "@/lib/portal/zeit";
import { nochNichtHier, useEcht } from "./quelle";
import type { FormField, SepaValue } from "@/lib/formulare/views";
import type { PortalInvoice, PortalMandate, PortalPerson, PortalVerein } from "@/lib/portal/views";
import { kopieren } from "./kopieren";

const OFFEN: InvoiceStatus[] = ["OPEN", "PARTIALLY_PAID", "OVERDUE"];
const offenBetrag = (r: PortalInvoice) => (OFFEN.includes(r.status) ? r.total - r.paidAmount : 0);

const SEPA_FELD: FormField = { key: "mandat", label: "SEPA-Lastschriftmandat", type: "sepa", required: true };

function Kopierzeile({ label, wert }: { label: string; wert: string }) {
  return (
    <div className="flex items-center gap-3 py-2">
      <div className="min-w-0 flex-1">
        <p className="text-caption text-text-tertiary">{label}</p>
        <p className="text-body-sm text-text-primary truncate tabular-nums">{wert}</p>
      </div>
      <button type="button" onClick={() => kopieren(wert, label)} aria-label={`${label} kopieren`} className="text-text-tertiary hover:bg-bg-secondary hover:text-text-primary grid h-8 w-8 place-items-center rounded-lg">
        <Copy className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
}

function Belegblatt({ r, mandat, verein, onClose }: { r: PortalInvoice; mandat: PortalMandate | undefined; verein: PortalVerein; onClose: () => void }) {
  const echt = useEcht();
  const offen = offenBetrag(r);
  const s = INVOICE_STATUS[r.status];
  return (
    <Seitenblatt
      offen
      onSchliessen={onClose}
      titel={`Rechnung ${r.number}`}
      breite="520px"
      kopfZusatz={
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-heading-sm text-text-primary">{r.title}</p>
            <TonBadge ton={s.ton}>{s.label}</TonBadge>
          </div>
          <p className="text-caption text-text-tertiary tabular-nums">
            {INVOICE_KIND[r.kind]} {r.number} · {r.personName}
          </p>
        </div>
      }
    >
      <div className="space-y-5">
        <dl className="text-body-sm grid grid-cols-2 gap-x-4 gap-y-2 tabular-nums">
          <dt className="text-text-tertiary">Datum</dt>
          <dd className="text-text-primary text-right">{formatDate(r.issueDate)}</dd>
          <dt className="text-text-tertiary">Fällig</dt>
          <dd className="text-text-primary text-right">{formatDate(r.dueDate)}</dd>
          <dt className="text-text-tertiary">Zahlart</dt>
          <dd className="text-text-primary text-right">{PAYMENT_METHOD[r.paymentMethod]}</dd>
        </dl>

        <div className="bg-bg-elevated shadow-e1 rounded-xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
          <ul className="divide-border-subtle divide-y">
            {r.items.map((p, i) => (
              <li key={i} className="text-body-sm flex items-start gap-3 px-4 py-2.5">
                <span className="text-text-primary min-w-0 flex-1">{p.title}</span>
                <span className="text-text-tertiary shrink-0 tabular-nums">{p.quantity} ×</span>
                <span className="text-text-primary w-20 shrink-0 text-right tabular-nums">{formatEuro(p.total)}</span>
              </li>
            ))}
          </ul>
          <div className="border-border text-body-sm space-y-1 border-t px-4 py-3 tabular-nums">
            <p className="flex justify-between">
              <span className="text-text-secondary">Gesamt</span>
              <span className="text-text-primary font-medium">{formatEuro(r.total)}</span>
            </p>
            {r.paidAmount > 0 && (
              <p className="flex justify-between">
                <span className="text-text-secondary">Bezahlt</span>
                <span className="text-success">{formatEuro(r.paidAmount)}</span>
              </p>
            )}
            {offen > 0 && (
              <p className="flex justify-between">
                <span className="text-text-secondary">Offen</span>
                <span className="text-text-primary font-semibold">{formatEuro(offen)}</span>
              </p>
            )}
          </div>
        </div>

        {r.status === "PAID" && (
          <p className="text-body-sm text-success flex items-center gap-2">
            <CircleCheck className="h-4 w-4" aria-hidden />
            Bezahlt am <span className="tabular-nums">{formatDate(r.paidAt)}</span>
            {r.paymentMethod === "SEPA" ? " per Lastschrift" : ""}
          </p>
        )}

        {offen > 0 && r.paymentMethod === "SEPA" && (
          <DialogNotice tone="neutral" icon={<Landmark className="text-text-tertiary h-4 w-4" aria-hidden />}>
            Wird am <span className="tabular-nums">{formatDate(r.collectionDate)}</span> abgebucht
            {mandat ? (
              <>
                {" "}
                vom Konto <span className="tabular-nums">{ibanVerdeckt(mandat.iban)}</span> (Mandat <span className="tabular-nums">{r.mandateReference}</span>, Gläubiger-ID{" "}
                <span className="tabular-nums">{verein.creditorId}</span>).
              </>
            ) : (
              "."
            )}{" "}
            Du musst nichts tun — diese Rechnung ist zugleich die Vorabankündigung.
          </DialogNotice>
        )}

        {offen > 0 && r.paymentMethod !== "SEPA" && (
          <div className="space-y-1">
            <p className="text-body-sm text-text-primary font-medium">Bitte überweisen</p>
            <div className="divide-border-subtle divide-y">
              <Kopierzeile label="Empfänger" wert={verein.name} />
              <Kopierzeile label="IBAN" wert={ibanLesbar(verein.iban)} />
              <Kopierzeile label="BIC" wert={verein.bic} />
              <Kopierzeile label="Betrag" wert={formatEuro(offen)} />
              <Kopierzeile label="Verwendungszweck" wert={r.number} />
            </div>
            <p className="text-caption text-text-tertiary pt-1">Der Kontoinhaber steht genau so da, wie die Bank ihn führt — die Empfängerprüfung deiner Bank zeigt dann keinen Hinweis.</p>
          </div>
        )}

        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            // BACKEND: PDF aus Invoice.documentId (eingefrorenes Blatt) mit Pruefung auf den Zugang.
            if (echt) return nochNichtHier("Das PDF", verein.email);
            toast("PDF wird vorbereitet", { description: `${r.number}.pdf` });
          }}
        >
          <Download className="h-4 w-4" aria-hidden />
          Als PDF
        </Button>
      </div>
    </Seitenblatt>
  );
}

function MandatTextDialog({ m, verein, onClose }: { m: PortalMandate; verein: PortalVerein; onClose: () => void }) {
  return (
    <Dialog open onClose={onClose} focusFirstField={false}>
      <DialogHeader>
        <DialogTitle>Mandat {m.reference}</DialogTitle>
        <DialogDescription>
          {MANDATE_TYPE[m.type]} · erteilt am <span className="tabular-nums">{formatDate(m.signedAt)}</span> ({m.signedVia === "Papier" ? "auf Papier" : "online"})
        </DialogDescription>
      </DialogHeader>
      <DialogContent>
        <dl className="text-body-sm grid grid-cols-[8rem_minmax(0,1fr)] gap-x-3 gap-y-1.5">
          <dt className="text-text-tertiary">Kontoinhaber</dt>
          <dd className="text-text-primary">{m.accountHolder}</dd>
          <dt className="text-text-tertiary">IBAN</dt>
          <dd className="text-text-primary font-mono tabular-nums">{ibanVerdeckt(m.iban)}</dd>
          {m.bic && (
            <>
              <dt className="text-text-tertiary">BIC</dt>
              <dd className="text-text-primary font-mono">{m.bic}</dd>
            </>
          )}
          <dt className="text-text-tertiary">Zuletzt genutzt</dt>
          <dd className="text-text-primary tabular-nums">{m.lastUsedAt ? formatDate(m.lastUsedAt) : "noch nicht"}</dd>
        </dl>
        <div className="bg-bg-secondary text-caption text-text-secondary space-y-2 rounded-lg px-3 py-2.5">
          {mandatsText({ name: verein.name, anschrift: verein.anschrift, creditorId: verein.creditorId }, m.reference).map((a, i) => (
            <p key={i} className={cn(i < 3 && "text-text-primary tabular-nums")}>
              {a}
            </p>
          ))}
        </div>
      </DialogContent>
      <DialogFooter>
        <DialogCancel>Schließen</DialogCancel>
      </DialogFooter>
    </Dialog>
  );
}

function NeuesMandatDialog({
  vorher,
  persons,
  verein,
  naechsteReferenz,
  onClose,
  onErteilt,
}: {
  vorher: PortalMandate | null;
  persons: PortalPerson[];
  verein: PortalVerein;
  naechsteReferenz: string;
  onClose: () => void;
  onErteilt: (m: PortalMandate) => void;
}) {
  const ich = persons.find((p) => p.relation === "SELF") ?? persons[0]!;
  const [wert, setWert] = useState<SepaValue>({ ...SEPA_LEER, holder: vorher?.accountHolder ?? ich.name });
  const [fehler, setFehler] = useState<string | undefined>();
  const [fuer, setFuer] = useState<string[]>(vorher?.contactIds ?? persons.map((p) => p.contactId));
  const echt = useEcht();

  async function erteilen(e: React.FormEvent) {
    e.preventDefault();
    const f = feldFehler(SEPA_FELD, wert);
    setFehler(f ?? undefined);
    if (f) return;
    if (fuer.length === 0) {
      toast.error("Bitte mindestens eine Person wählen, deren Beiträge abgebucht werden.");
      return;
    }
    // BACKEND: SepaMandate anlegen (ONLINE, Nachweis mit Zeitpunkt und Textfassung, amendedFrom), altes beenden,
    // Zahlart der gewaehlten Personen auf SEPA stellen; Mail mit Mandatsbestaetigung.
    if (echt) {
      nochNichtHier("Ein neues Mandat", verein.email);
      return onClose();
    }
    onErteilt({
      id: `m-neu-${Date.now()}`,
      reference: naechsteReferenz,
      iban: wert.iban.replace(/\s+/g, ""),
      bic: wert.bic || null,
      accountHolder: wert.holder,
      signedAt: new Date().toISOString().slice(0, 10),
      type: "CORE",
      status: "ACTIVE",
      lastUsedAt: null,
      signedVia: "Online",
      contactIds: fuer,
    });
    toast.success("Neues Mandat erteilt", {
      description: `Referenz ${naechsteReferenz}. Ab dem nächsten Einzug gilt ${ibanVerdeckt(wert.iban)}${vorher ? `; das Mandat ${vorher.reference} endet damit` : ""}.`,
    });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} size="default">
      <form onSubmit={erteilen} className="flex min-h-0 flex-1 flex-col" noValidate>
        <DialogHeader>
          <DialogTitle>{vorher ? "Bankverbindung ändern" : "Lastschrift einrichten"}</DialogTitle>
          <DialogDescription>
            {vorher
              ? "Eine neue Bankverbindung ist ein neues Mandat mit neuer Referenz. Bis es gilt, bucht der Verein wie bisher ab."
              : `Der Verein bucht Beiträge und Rechnungen ab — mit Ankündigung ${vorabTage()} Tage vorher.`}
          </DialogDescription>
        </DialogHeader>
        <DialogContent>
          <SepaEingabe
            value={wert}
            onChange={(v) => {
              setWert(v);
              setFehler(undefined);
            }}
            error={fehler}
            glaeubiger={{ name: verein.name, anschrift: verein.anschrift, creditorId: verein.creditorId }}
            referenz={naechsteReferenz}
          />
          {persons.length > 1 && (
            <div className="space-y-2">
              <p className="text-body-sm text-text-primary font-medium">Für die Beiträge von</p>
              {persons.map((p) => (
                <Checkbox
                  key={p.contactId}
                  id={`mandat-fuer-${p.contactId}`}
                  checked={fuer.includes(p.contactId)}
                  onChange={(e) => setFuer((f) => (e.target.checked ? [...f, p.contactId] : f.filter((x) => x !== p.contactId)))}
                  label={p.name}
                  hint={p.fee ? `${p.fee.name} · bisher ${PAYMENT_METHOD[p.paymentMethod]}` : undefined}
                />
              ))}
            </div>
          )}
        </DialogContent>
        <DialogFooter>
          <DialogCancel />
          <DialogSubmit>Mandat erteilen</DialogSubmit>
        </DialogFooter>
      </form>
    </Dialog>
  );
}

export function Beitraege({
  rechnungen,
  mandate: anfangsMandate,
  persons,
  verein,
}: {
  rechnungen: PortalInvoice[];
  mandate: PortalMandate[];
  persons: PortalPerson[];
  verein: PortalVerein;
}) {
  const echt = useEcht();
  const [filter, setFilter] = useState<"offen" | "bezahlt" | "alle">("alle");
  const [person, setPerson] = useState<string | null>(null);
  const [blatt, setBlatt] = useState<PortalInvoice | null>(null);
  const [mandate, setMandate] = useState(anfangsMandate);
  const [zeigeText, setZeigeText] = useState<PortalMandate | null>(null);
  const [neu, setNeu] = useState<{ vorher: PortalMandate | null } | null>(null);

  const gueltig = mandate.filter((m) => m.status === "ACTIVE");
  const offen = rechnungen.filter((r) => offenBetrag(r) > 0);
  const summe = offen.reduce((s, r) => s + offenBetrag(r), 0);
  const gezeigt = rechnungen
    .filter((r) => (filter === "offen" ? offenBetrag(r) > 0 : filter === "bezahlt" ? r.status === "PAID" : true))
    .filter((r) => !person || r.contactId === person);
  const mandatFuer = (contactId: string) => gueltig.find((m) => m.contactIds.includes(contactId));

  async function widerrufen(m: PortalMandate) {
    const ja = await confirmDialog({
      title: `Mandat ${m.reference} widerrufen?`,
      description: "Der Verein bucht danach nichts mehr ab. Offene und künftige Beiträge kommen als Rechnung zum Überweisen.",
      confirmLabel: "Widerrufen",
      danger: true,
    });
    if (!ja) return;
    // BACKEND: SepaMandate.status = REVOKED, revokedAt; Zahlart der Personen auf Ueberweisung.
    if (echt) return nochNichtHier("Ein Widerruf", verein.email);
    setMandate((ms) => ms.map((x) => (x.id === m.id ? { ...x, status: "REVOKED" } : x)));
    toast("Mandat widerrufen", { description: "Künftige Beiträge kommen als Rechnung." });
  }

  const naechsteReferenz = (() => {
    const ich = persons.find((p) => p.relation === "SELF");
    const n = mandate.length + 1;
    return `GA-${ich?.memberNumber ?? "0000"}-${String(n).padStart(2, "0")}`;
  })();

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 items-start gap-5 lg:grid-cols-2">
        <Card>
          <CardContent className="space-y-3 p-5">
            {summe > 0 ? (
              <>
                <p className="text-caption text-text-tertiary">Offen</p>
                <p className="text-display-lg text-text-primary tabular-nums">{formatEuro(summe)}</p>
                <ul className="space-y-1.5">
                  {offen.map((r) => (
                    <li key={r.id}>
                      <button type="button" onClick={() => setBlatt(r)} className="text-body-sm hover:text-accent flex w-full items-center gap-2 text-left">
                        <span className="text-text-primary min-w-0 flex-1 truncate">{r.title}</span>
                        <span className="text-text-tertiary shrink-0">
                          {r.paymentMethod === "SEPA" && r.collectionDate ? `Abbuchung ${tagKurz(r.collectionDate)}` : `fällig ${tagKurz(r.dueDate)}`}
                        </span>
                        <ArrowRight className="text-text-tertiary h-3.5 w-3.5 shrink-0" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <p className="text-body-md text-text-primary flex items-center gap-2">
                <CircleCheck className="text-success h-5 w-5" aria-hidden />
                Alles bezahlt.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitleRow title="Beitrag je Person" />
          </CardHeader>
          <CardContent>
            <ul className="divide-border-subtle divide-y">
              {persons.map((p) => {
                const m = mandatFuer(p.contactId);
                return (
                  <li key={p.contactId} className="text-body-sm flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                    <span className="min-w-0 flex-1">
                      <span className="text-text-primary block font-medium">{p.firstName}</span>
                      <span className="text-caption text-text-tertiary block">
                        {m ? `Lastschrift · Mandat ${m.reference}` : PAYMENT_METHOD[p.paymentMethod]}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="text-text-primary block">{p.fee?.name ?? "kein Beitrag"}</span>
                      {p.fee && (
                        <span className="text-caption text-text-tertiary block tabular-nums">
                          {p.fee.amount > 0 ? `${formatEuro(p.fee.amount)} ${FEE_INTERVAL[p.fee.interval]}` : "beitragsfrei"}
                        </span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
            {persons.some((p) => p.fee?.name === "Familie") && (
              <p className="text-caption text-text-tertiary mt-3">Der Familienbeitrag gilt für den ganzen Haushalt und kommt als eine Rechnung.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <section aria-labelledby="belege" className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 id="belege" className="text-heading-sm text-text-primary mr-auto">
            Rechnungen
          </h2>
          {persons.length > 1 && (
            <Schiene>
              <Zaehlknopf label="Alle" anzahl={rechnungen.length} aktiv={person === null} onClick={() => setPerson(null)} />
              {persons.map((p) => (
                <Zaehlknopf key={p.contactId} label={p.firstName} anzahl={rechnungen.filter((r) => r.contactId === p.contactId).length} aktiv={person === p.contactId} onClick={() => setPerson(person === p.contactId ? null : p.contactId)} />
              ))}
            </Schiene>
          )}
          <Schiene>
            <Zaehlknopf label="Offen" anzahl={offen.length} aktiv={filter === "offen"} ton="gefahr" onClick={() => setFilter(filter === "offen" ? "alle" : "offen")} />
            <Zaehlknopf label="Bezahlt" anzahl={rechnungen.filter((r) => r.status === "PAID").length} aktiv={filter === "bezahlt"} onClick={() => setFilter(filter === "bezahlt" ? "alle" : "bezahlt")} />
          </Schiene>
        </div>

        {gezeigt.length === 0 ? (
          <EmptyState icon={FileText} title={rechnungen.length === 0 ? "Noch keine Rechnungen" : "Nichts in dieser Auswahl"} description={rechnungen.length === 0 ? "Die erste Beitragsrechnung kommt mit dem nächsten Beitragslauf." : "Ändere den Filter, um weitere Rechnungen zu sehen."} />
        ) : (
          <ul className="bg-bg-elevated shadow-e2 divide-border-subtle divide-y rounded-2xl ring-1 ring-black/[0.04] dark:ring-white/[0.06]">
            {gezeigt.map((r) => {
              const s = INVOICE_STATUS[r.status];
              return (
                <li key={r.id}>
                  <button type="button" onClick={() => setBlatt(r)} className="hover:bg-bg-secondary flex w-full items-center gap-3 px-4 py-3 text-left first:rounded-t-2xl last:rounded-b-2xl">
                    <span className="min-w-0 flex-1">
                      <span className="text-body-md text-text-primary block truncate">{r.title}</span>
                      <span className="text-caption text-text-tertiary block truncate tabular-nums">
                        {r.number} · {r.personName.split(" ")[0]} · {formatDate(r.issueDate)}
                      </span>
                    </span>
                    <span className="shrink-0 text-right">
                      <span className="text-body-md text-text-primary block tabular-nums">{formatEuro(r.total)}</span>
                      <TonBadge ton={s.ton} className="mt-0.5">
                        {s.label}
                      </TonBadge>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section aria-labelledby="mandat" className="space-y-3">
        <h2 id="mandat" className="text-heading-sm text-text-primary">
          Lastschrift
        </h2>
        {mandate.length === 0 ? (
          <Card>
            <CardContent className="flex flex-wrap items-center gap-3 p-5">
              <Landmark className="text-text-tertiary h-5 w-5" aria-hidden />
              <p className="text-body-sm text-text-secondary min-w-0 flex-1">Noch kein Mandat. Mit Lastschrift verpasst du keine Fälligkeit — jeder Einzug wird vorher angekündigt.</p>
              <Button type="button" onClick={() => setNeu({ vorher: null })}>
                Lastschrift einrichten
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {mandate.map((m) => {
              const st = MANDATE_STATUS[m.status];
              const fuer = persons.filter((p) => m.contactIds.includes(p.contactId)).map((p) => p.firstName);
              return (
                <Card key={m.id} className={cn(m.status !== "ACTIVE" && "opacity-70")}>
                  <CardContent className="space-y-3 p-5">
                    <div className="flex flex-wrap items-start gap-3">
                      <ShieldCheck className={cn("mt-0.5 h-5 w-5 shrink-0", m.status === "ACTIVE" ? "text-success" : "text-text-tertiary")} aria-hidden />
                      <div className="min-w-0 flex-1">
                        <p className="text-body-md text-text-primary font-medium tabular-nums">{ibanVerdeckt(m.iban)}</p>
                        <p className="text-caption text-text-tertiary tabular-nums">
                          {m.accountHolder} · Mandat {m.reference} · seit {formatDate(m.signedAt)}
                          {fuer.length ? ` · für ${fuer.join(", ")}` : ""}
                        </p>
                      </div>
                      <TonBadge ton={st.ton}>{m.status === "REVOKED" && mandate.some((x) => x.status === "ACTIVE" && x.id !== m.id) ? "Ersetzt" : st.label}</TonBadge>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" size="sm" onClick={() => setZeigeText(m)}>
                        Mandat ansehen
                      </Button>
                      {m.status === "ACTIVE" && (
                        <>
                          <Button type="button" variant="secondary" size="sm" onClick={() => setNeu({ vorher: m })}>
                            Bankverbindung ändern
                          </Button>
                          <Button type="button" variant="ghost" size="sm" className="text-text-secondary" onClick={() => widerrufen(m)}>
                            Widerrufen
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
            {gueltig.length === 0 && (
              <Button type="button" variant="secondary" onClick={() => setNeu({ vorher: null })}>
                Neue Lastschrift einrichten
              </Button>
            )}
          </div>
        )}
        <p className="text-caption text-text-tertiary">
          Gläubiger-ID des Vereins <span className="tabular-nums">{verein.creditorId}</span>. Jeden Einzug kündigen wir mindestens {vorabTage()} Tage vorher an; eine Lastschrift kannst du bei deiner Bank innerhalb von {erstattungWochen()} Wochen zurückgeben.
        </p>
      </section>

      {blatt && <Belegblatt r={blatt} mandat={mandate.find((m) => m.reference === blatt.mandateReference)} verein={verein} onClose={() => setBlatt(null)} />}
      {zeigeText && <MandatTextDialog m={zeigeText} verein={verein} onClose={() => setZeigeText(null)} />}
      {neu && (
        <NeuesMandatDialog
          vorher={neu.vorher}
          persons={persons}
          verein={verein}
          naechsteReferenz={naechsteReferenz}
          onClose={() => setNeu(null)}
          onErteilt={(m) => setMandate((ms) => [m, ...ms.map((x) => (neu.vorher && x.id === neu.vorher.id ? { ...x, status: "REVOKED" as const } : x))])}
        />
      )}
    </div>
  );
}
